import { FC, useMemo } from 'react';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { MasonryGrid } from '../../../ui/layout/MasonryGrid/MasonryGrid';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { PostCollectionCardGrid } from '../../../ui/posts/PostCardGrid/PostCollectionCardGrid';
import { EventSource } from '../../../utils/analyticsService';
import { ROBOT_IMAGE_ALIAS } from '../utils/constants';
import { getPostGridLink } from '../utils/getPostGridLink';
import { withPostCallbacks } from '../withPostCallbacks';
import { FeedTitle } from './FeedTitle';
import { PostsGridContentProps } from './types';
import type { PostModel } from '../../../data-layer/posts/types';

type RobotPostsGridContentProps = Pick<
    PostsGridContentProps,
    'collection' | 'excludePostsIds' | 'eventSource' | 'titleEnabled'
>;

/** Лента для робота: без виртуализации, рекламы и догрузки — всё это требует JS */
export const RobotPostsGridContent: FC<RobotPostsGridContentProps> = ({
    collection,
    excludePostsIds,
    titleEnabled = false,
    eventSource = EventSource.POST,
}) => {
    const EnchancedPostGridCard = useMemo(
        () => withPostCallbacks(PostCardGrid, eventSource),
        [eventSource],
    );

    const EnhancedCollectionCardGrid = useMemo(
        () => withPostCallbacks(PostCollectionCardGrid, eventSource),
        [eventSource],
    );

    const posts = collection.items.filter((post) => !excludePostsIds?.includes(post.id));

    const renderItem = (post: PostModel, index: number) => {
        const { href, isExternal } = getPostGridLink(post);

        if (post.postType === 'collection') {
            return (
                <EnhancedCollectionCardGrid
                    post={post}
                    index={index}
                    href={href}
                    imageAlias={ROBOT_IMAGE_ALIAS}
                />
            );
        }

        return (
            <EnchancedPostGridCard
                post={post}
                index={index}
                href={href}
                isExternalHref={isExternal}
                imageAlias={ROBOT_IMAGE_ALIAS}
                isCompact
                withProductsPreview
            />
        );
    };

    return (
        <Flex width="100%" pt={titleEnabled ? 0 : 2}>
            {titleEnabled ? <FeedTitle hasItems={posts.length > 0} /> : null}

            <Box width="100%" px={{ xs: 0.5, lg: 0 }}>
                <MasonryGrid items={posts} renderItem={renderItem} />
            </Box>
        </Flex>
    );
};
